"use client";

import { useCallback, useRef, useState } from "react";
import { useIntegrationLink } from "@/hooks/integration-api/useIntegration";
import type { IntegrationLinkKey } from "@/utils/constants/services/serviceCatalog";

type PartnerDialogState = "closed" | "loading" | "error";

export const useHeroIntegrationPartner = () => {
  const { getIntegrationLink, isGettingIntegrationLink } = useIntegrationLink();
  const [partnerDialogState, setPartnerDialogState] =
    useState<PartnerDialogState>("closed");
  const [activeIntegrationKey, setActiveIntegrationKey] =
    useState<IntegrationLinkKey | null>(null);
  const integrationRequestIdRef = useRef(0);

  /**
   * Start partner redirect flow
   * @param integrationKey Integration link key
   */
  const startPartnerRedirectFlow = useCallback(
    async (integrationKey: IntegrationLinkKey) => {

      const requestId = ++integrationRequestIdRef.current;
      setActiveIntegrationKey(integrationKey);
      setPartnerDialogState("loading");

      const result = await getIntegrationLink(integrationKey);

      if (integrationRequestIdRef.current !== requestId) {
        return;
      }

      if (result.success) {
        setPartnerDialogState("closed");
        setActiveIntegrationKey(null);
        window.open(result.link, "_blank", "noopener,noreferrer");
        return;
      }

      setPartnerDialogState((currentState) =>
        currentState === "closed" ? "closed" : "error",
      );
    },
    [getIntegrationLink],
  );

  const closePartnerModal = useCallback(() => {
    integrationRequestIdRef.current += 1;
    setPartnerDialogState("closed");
    setActiveIntegrationKey(null);
  }, []);

  const retryPartnerRedirectFlow = useCallback(() => {
    if (!activeIntegrationKey) {
      return;
    }

    void startPartnerRedirectFlow(activeIntegrationKey);
  }, [activeIntegrationKey, startPartnerRedirectFlow]);

  return {
    activeIntegrationKey,
    closePartnerModal,
    isOpeningIntegrationLink: isGettingIntegrationLink,
    startPartnerRedirectFlow,
    partnerDialogState,
    retryPartnerRedirectFlow,
  };
};
